import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import i18n from 'i18next';
import { env } from '../config/env';
import { useAuthStore } from '../stores/authStore';

// Re-export queryKeys for backward compatibility
export { queryKeys, getResourceKeys } from './queryKeys';

// API Client Configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Token refresh state management
let isRefreshing = false;
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Helper functions for token storage
function getTokenStorage() {
  // Check if user has "remember me" enabled
  const rememberMe = localStorage.getItem('remember_me') === 'true';
  return rememberMe ? localStorage : sessionStorage;
}

function getToken(key: string): string | null {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

function setToken(key: string, value: string): void {
  const storage = getTokenStorage();
  storage.setItem(key, value);
}

function removeToken(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

function clearAllTokens(): void {
  removeToken('auth_token');
  removeToken('refresh_token');
  removeToken('user');
  localStorage.removeItem('remember_me');
  // Clear Zustand persist storage to prevent stale isAuthenticated state
  localStorage.removeItem('auth-storage');
  sessionStorage.removeItem('auth-storage');
  // Reset Zustand store state so GuestGuard doesn't redirect away from login
  useAuthStore.getState().logout();
}

// Process queued requests after successful refresh
function processQueue(error: any, token: string | null = null) {
  failedRequestsQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });

  failedRequestsQueue = [];
}

// Refresh token function
async function refreshAccessToken(): Promise<string> {
  const refreshToken = getToken('refresh_token');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post(
      `${env.apiUrl}/api/v1/auth/refresh`,
      { refresh_token: refreshToken }, // Backend expects snake_case
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );

    // Backend returns wrapped response: { success, data: { access_token, refresh_token }, meta }
    const responseData = response.data.data || response.data;
    const { access_token: newAccessToken, refresh_token: newRefreshToken } = responseData;

    // Save new tokens to the appropriate storage
    setToken('auth_token', newAccessToken);
    if (newRefreshToken) {
      setToken('refresh_token', newRefreshToken);
    }

    return newAccessToken;
  } catch (error) {
    // Clear all auth data on refresh failure
    clearAllTokens();
    throw error;
  }
}

// Request interceptor for API versioning and auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Rewrite /api/ to /api/v1/ for versioned endpoints
    if (config.url?.startsWith('/api/') && !config.url.startsWith('/api/v1/') && !config.url.startsWith('/api/docs')) {
      config.url = config.url.replace('/api/', '/api/v1/');
    }

    const token = getToken('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.headers && i18n.language) {
      config.headers['Accept-Language'] = i18n.language;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = getToken('refresh_token');

      // If no refresh token, clear auth and redirect
      if (!refreshToken) {
        clearAllTokens();
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: (err: any) => {
              reject(err);
            }
          });
        });
      }

      // Mark as retry to prevent infinite loops
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const newAccessToken = await refreshAccessToken();

        // Process queued requests with new token
        processQueue(null, newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear queue and redirect to login
        processQueue(refreshError, null);
        clearAllTokens();

        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Don't retry on 403
    if (error.response?.status === 403) {
      return Promise.reject(error);
    }

    // Implement retry logic for network errors and 5xx errors
    const shouldRetry = (
      !error.response ||
      (error.response.status >= 500 && error.response.status < 600)
    );

    if (shouldRetry && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Exponential backoff: wait 1s before retry
      await new Promise(resolve => setTimeout(resolve, 1000));

      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

// Generic fetch function
export async function fetchData<T>(endpoint: string): Promise<T> {
  const response = await apiClient.get<any>(endpoint);
  // Unwrap standardized API response format: { success, data, meta }
  // Return the inner data if it exists, otherwise return response.data directly (backward compatible)
  return response.data.data !== undefined ? response.data.data : response.data;
}

// Generic mutation function
export async function mutateData<TData, TVariables>(
  endpoint: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  data?: TVariables
): Promise<TData> {
  const response = await apiClient.request<any>({
    url: endpoint,
    method,
    data
  });
  // Unwrap standardized API response format: { success, data, meta }
  // Return the inner data if it exists, otherwise return response.data directly (backward compatible)
  return response.data.data !== undefined ? response.data.data : response.data;
}

// Custom hooks for common operations
export function useApiQuery<TData>(
  key: readonly unknown[],
  endpoint: string,
  options?: Omit<UseQueryOptions<TData, AxiosError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TData, AxiosError>({
    queryKey: key,
    queryFn: () => fetchData<TData>(endpoint),
    ...options
  });
}

export function useApiMutation<TData, TVariables = unknown>(
  endpoint: string | ((data: TVariables) => string),
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  options?: {
    onSuccess?: (data: TData) => void;
    onError?: (error: AxiosError) => void;
    invalidateKeys?: readonly unknown[][];
  }
) {
  const queryClient = useQueryClient();

  return useMutation<TData, AxiosError, TVariables>({
    mutationFn: (variables) => {
      const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(variables) : endpoint;
      return mutateData<TData, TVariables>(resolvedEndpoint, method, variables);
    },
    onSuccess: (data) => {
      // Invalidate related queries
      options?.invalidateKeys?.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError
  });
}

// Type-safe API error handler
// Unwraps API envelope: { success: false, error: { code, message, details } }
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    return data?.error?.message || data?.message || error.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

export { apiClient };
